import { useState, useEffect, useCallback, useMemo } from 'react';
import { useFeatureFlag } from './useFeatureFlag';
// Removed Redux auth selectors - now using useAuth() hook
import { getClockEvents } from '../services/clockEventService';
import { useUserData } from '../hooks/useUserData';

export const useClockInStatus = (facilityId) => {
  // const user = useAuth().user; // Now using useAuth() hook
// const loginComplete = true; // No longer needed with Firebase-only auth
const { user, defaultFacility } = useUserData();

  // Consider facilities loaded when user has defaultFacility
  const facilitiesLoaded = !!defaultFacility;
  // Login is complete when user is available
  const loginComplete = !!user;
  
  const [isClocked, setIsClockedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastClockEvent, setLastClockEvent] = useState(null);
  const [requiresClockIn, setRequiresClockIn] = useState(false);
  
  // Check if scheduling feature is enabled for the facility
  // Always call useFeatureFlag to follow React hooks rules, but pass null when no facilityId
  const { isEnabled: schedulingEnabled, loading: featureLoading } = useFeatureFlag(
    'sch', 
    facilityId
  );
  
  // Memoize values to prevent infinite re-renders
  const memoizedFacilityId = useMemo(() => facilityId, [facilityId]);
  const memoizedSchedulingEnabled = useMemo(() => schedulingEnabled, [schedulingEnabled]);
  
  // Don't proceed with clock status checks without proper context
  const shouldCheckClockStatus = loginComplete && facilitiesLoaded && memoizedFacilityId && !featureLoading;

  // Function to refresh status (can be called after clock in/out)
  const refreshStatus = useCallback(async () => {
    console.log('🔄 Refreshing clock status...', { facilityId: memoizedFacilityId, userId: user?.uid });
    
    // Don't proceed without proper user and facility context
    if (!user?.uid || !shouldCheckClockStatus) {
      console.log('⚠️ Clock status refresh skipped - missing context', { 
        userId: user?.uid, 
        shouldCheckClockStatus, 
        loginComplete, 
        facilitiesLoaded 
      });
      // Keep loading if we're still waiting for login completion or facilities
      if (!loginComplete || !facilitiesLoaded) {
        setLoading(true);
      } else {
        setLoading(false);
      }
      return;
    }

    setLoading(true);

    // If scheduling is not enabled, don't require clock-in
    if (!memoizedSchedulingEnabled) {
      console.log('📅 Scheduling not enabled - allowing access');
      setRequiresClockIn(false);
      setIsClockedIn(true); // Allow access
      setLoading(false);
      return;
    }

    setRequiresClockIn(true);

    try {
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000);

      const filters = {
        employeeId: user.uid,
        facilityId: memoizedFacilityId,
        startDate: startOfDay.toISOString(),
        endDate: endOfDay.toISOString()
      };

      const result = await getClockEvents(memoizedFacilityId, filters, 1, 1);
      
      if (result.clockEvents && result.clockEvents.length > 0) {
        const lastEvent = result.clockEvents[0];
        setLastClockEvent(lastEvent);
        const newIsClockedIn = lastEvent.action === 'clock_in';
        setIsClockedIn(newIsClockedIn);
        console.log('✅ Clock status refreshed:', { 
          action: lastEvent.action, 
          isClockedIn: newIsClockedIn,
          timestamp: lastEvent.timestamp
        });
      } else {
        setLastClockEvent(null);
        setIsClockedIn(false);
        console.log('❌ No clock events found for today');
      }
    } catch (error) {
      console.error('❌ Error checking clock-in status:', error);
      setIsClockedIn(false);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, shouldCheckClockStatus, memoizedSchedulingEnabled, memoizedFacilityId, loginComplete, facilitiesLoaded]);

  useEffect(() => {
    const checkClockInStatus = async () => {
      // Don't proceed without proper user and facility context
      if (!user?.uid || !shouldCheckClockStatus) {
        // Keep loading if we're still waiting for login completion or facilities
        if (!loginComplete || !facilitiesLoaded) {
          setLoading(true);
        } else {
          setLoading(false);
        }
        return;
      }

      // If scheduling is not enabled, don't require clock-in
      if (!memoizedSchedulingEnabled) {
        setRequiresClockIn(false);
        setIsClockedIn(true); // Allow access
        setLoading(false);
        return;
      }

      setRequiresClockIn(true);

      try {
        const today = new Date();
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const endOfDay = new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000);

        // Check for today's clock events using service
        const filters = {
          employeeId: user.uid,
          facilityId: memoizedFacilityId,
          startDate: startOfDay.toISOString(),
          endDate: endOfDay.toISOString()
        };

        const result = await getClockEvents(memoizedFacilityId, filters, 1, 1);
        
        if (result.clockEvents && result.clockEvents.length > 0) {
          const lastEvent = result.clockEvents[0];
          setLastClockEvent(lastEvent);
          setIsClockedIn(lastEvent.action === 'clock_in');
        } else {
          setLastClockEvent(null);
          setIsClockedIn(false);
        }
      } catch (error) {
        console.error('Error checking clock-in status:', error);
        setIsClockedIn(false);
      } finally {
        setLoading(false);
      }
    };

    checkClockInStatus();
  }, [user?.uid, shouldCheckClockStatus, memoizedSchedulingEnabled, memoizedFacilityId, loginComplete, facilitiesLoaded]);

  // Listen for clock-in status changes with improved event handling
  useEffect(() => {
    const handleStatusChange = (event) => {
      console.log('🔔 Clock status change event received:', event.detail);
      
      // If we have immediate status info from the event, update state immediately for better UX
      if (event.detail && typeof event.detail.isClockedIn === 'boolean') {
        setIsClockedIn(event.detail.isClockedIn);
        console.log('⚡ Immediate state update:', { isClockedIn: event.detail.isClockedIn });
      }
      
      // Still refresh from database to ensure consistency
      setTimeout(() => {
        refreshStatus();
      }, 100); // Reduced delay for faster response
    };

    // Listen for the standard event
    window.addEventListener('clockInStatusChanged', handleStatusChange);
    
    // Also listen for a more specific event with data
    window.addEventListener('clockStatusUpdate', handleStatusChange);
    
    return () => {
      window.removeEventListener('clockInStatusChanged', handleStatusChange);
      window.removeEventListener('clockStatusUpdate', handleStatusChange);
    };
  }, [refreshStatus]);

  // Force refresh function that components can call
  const forceRefresh = useCallback(() => {
    console.log('🔄 Force refresh requested');
    refreshStatus();
  }, [refreshStatus]);

  return {
    isClockedIn: isClocked,
    loading,
    lastClockEvent,
    requiresClockIn,
    refreshStatus,
    forceRefresh
  };
}; 